import axios from 'axios';
import { gewaehlteFirma, merkeFirma } from '@/lib/firma';
import { EINRICHTEN_PFAD, verlangtEinrichtung } from '@/lib/zwei-faktor';

/** In-memory access token — never stored in localStorage */
let accessToken: string | null = null;

export function setAccessToken(token: string): void {
  accessToken = token;
}

export function clearAccessToken(): void {
  accessToken = null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL ?? 'http://localhost:3000'}/api/v1`,
  withCredentials: true, // send httpOnly refresh-token cookie
  headers: {
    'Accept-Language': 'de', // nestjs-i18n: Backend-Meldungen auf Deutsch
  },
});

// ── Request: attach bearer token ──────────────────────────────────────────────
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ── Response: silent token refresh on 401 ────────────────────────────────────
let isRefreshing = false;
let queue: Array<(token: string) => void> = [];

function drain(token: string) {
  queue.forEach((cb) => cb(token));
  queue = [];
}

/**
 * Public routes that must never be redirected to /login,
 * even when a 401 occurs (e.g. no refresh-cookie present for new employees).
 */
// AP-46 22./23.: Bestätigung per Link und Prüfseite — ohne Anmeldung; AP-48 RN-6: Sammel-Gegenzeichnung.
const PUBLIC_ROUTES = ['/login', '/accept-invite', '/reset-password', '/forgot-password', EINRICHTEN_PFAD, '/bestaetigen/', '/pruefen/', '/gegenzeichnen/'];

/**
 * AP-38-B: Erneuert den Zugriffstoken **für die Firma dieses Tabs**.
 *
 * Antwortet der Server mit einer anderen Firma, gilt die gewählte nicht mehr
 * (Zugang entzogen, Mandant gesperrt). Dann wird neu geladen: Die Seite zeigt
 * sonst Daten der alten Firma, während die nächsten Anfragen schon die neue
 * bedienen.
 */
async function erneuere(): Promise<string> {
  const gewaehlt = gewaehlteFirma();
  const { data } = await api.post<{ accessToken: string; tenantId: string }>(
    '/auth/refresh',
    gewaehlt ? { tenantId: gewaehlt } : {},
  );
  if (data.tenantId) {
    merkeFirma(data.tenantId);
    if (gewaehlt && data.tenantId !== gewaehlt) window.location.reload();
  }
  return data.accessToken;
}
const isPublicRoute = () =>
  PUBLIC_ROUTES.some((r) => window.location.pathname.startsWith(r));

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as typeof error.config & { _retry?: boolean };

    // AP-41: Der Betrieb hat eine Vertragsfrist versäumt — das Layout lädt den Stand neu und
    // zeigt den Sperrbildschirm. Ein Ereignis statt eines Imports, damit `lib/vertraege.ts`
    // (das selbst `api` benutzt) keinen Kreis bildet.
    if (error.response?.status === 403 && error.response.data?.code === 'CONTRACT_ACCEPTANCE_REQUIRED') {
      window.dispatchEvent(new Event('symphenia:vertragssperre'));
    }

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // Don't try to refresh if the failing request was the refresh itself
    if (original.url?.includes('/auth/refresh')) {
      clearAccessToken();
      // Guard: don't redirect on public auth pages (accept-invite, reset-password, …)
      if (!isPublicRoute()) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve) => {
        queue.push(resolve);
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const neu = await erneuere();
      accessToken = neu;
      drain(neu);
      original.headers.Authorization = `Bearer ${neu}`;
      return api(original);
    } catch (fehler) {
      clearAccessToken();
      queue = [];
      // AP-38-E: Nach der Frist verlangt das Erneuern die Einrichtung (403 mit Ticket).
      if (verlangtEinrichtung(fehler)) return Promise.reject(error);
      // Guard: don't redirect on public auth pages (accept-invite, reset-password, …)
      if (!isPublicRoute()) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);
